import { ChangeEvent, FC, FocusEventHandler, memo, useMemo } from 'react'
import { EstimatedProjectValueBand, allEstimatedProjectValueBands } from '@leadgen/models/client'
import Box from '@mui/material/Box'
import { CheckboxButton } from '../../../atoms/CheckboxButton'

export const estimatedProjectValueBandMap: Record<
  EstimatedProjectValueBand,
  { title: string; description: string }
> = {
  'under-10k': {
    title: 'Under £10k',
    description: 'Projects estimated below £10,000.',
  },
  '10k-25k': {
    title: '£10k-£25k',
    description: 'Projects estimated between £10,000 and £25,000.',
  },
  '25k-50k': {
    title: '£25k-£50k',
    description: 'Projects estimated between £25,000 and £50,000.',
  },
  '50k-100k': {
    title: '£50k-£100k',
    description: 'Projects estimated between £50,000 and £100,000.',
  },
  '100k-250k': {
    title: '£100k-£250k',
    description: 'Projects estimated between £100,000 and £250,000.',
  },
  '250k-500k': {
    title: '£250k-£500k',
    description: 'Projects estimated between £250,000 and £500,000.',
  },
  '500k-1m': {
    title: '£500k-£1m',
    description: 'Projects estimated between £500,000 and £1,000,000.',
  },
  '1m-plus': {
    title: '£1m+',
    description: 'Projects estimated above £1,000,000.',
  },
}

const EstimatedProjectValueBandGrid: FC<{
  estimatedProjectValueBands: EstimatedProjectValueBand[]
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  onBlur?: FocusEventHandler<HTMLButtonElement>
}> = ({ estimatedProjectValueBands, onChange, onBlur }) => {
  const selectedEstimatedProjectValueBandsSet = useMemo(
    () => new Set(estimatedProjectValueBands),
    [estimatedProjectValueBands],
  )
  return (
    <Box
      component={'ul'}
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 2,
        padding: 2,
        margin: 0,
      }}
    >
      {allEstimatedProjectValueBands.map(estimatedProjectValueBand => (
        <CheckboxButton
          key={estimatedProjectValueBand}
          onBlur={onBlur}
          onChange={onChange}
          value={estimatedProjectValueBand}
          checked={selectedEstimatedProjectValueBandsSet.has(estimatedProjectValueBand)}
          label={estimatedProjectValueBandMap[estimatedProjectValueBand].title}
          tooltip={estimatedProjectValueBandMap[estimatedProjectValueBand].description}
        />
      ))}
    </Box>
  )
}

export default memo(EstimatedProjectValueBandGrid)
